import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { useUserRoles } from '@Hooks/useUserRoles';
import type { UserRoleBasic } from '@Types/userRole';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { useNavigate } from 'react-router-dom';
import UserRoleMenu from './userRoleMenu';

// ---------- column configs ----------

const userRoleColumnConfigs: ColumnConfig<UserRoleBasic>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name,
    type: 'text',
    size: 120,
  },
  {
    id: 'description',
    headerLabel: 'Description',
    accessor: (row) => row.description ?? '',
    type: 'text',
  },
  {
    id: 'id',
    headerLabel: '',
    accessor: (row) => row.id,
    type: 'text',
    size: 80,
    cell: (info) => {
      return <UserRoleMenu data={info.row?.original} />;
    },
    enableSorting: false,
    hideFromFilters: true,
    meta: {
      dataNoClick: true,
    },
  },
];

const USER_ROLE_FIELD_MAPPING: Record<string, string> = {
  name: 'filter[name]',
  description: 'filter[description]',
};

const USER_ROLE_STATIC_PARAMS: Record<string, string> = {
  '[filter][deletedAt]': 'null',
};

const UserRoleList = () => {
  const navigate = useNavigate();

  const controller = useResourceTableController<UserRoleBasic>({
    columnsConfig: userRoleColumnConfigs,
    defaultSort: {
      id: 'name',
      desc: false,
    },
    baseSearchParams: () => ({}),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: USER_ROLE_FIELD_MAPPING,
        staticParams: USER_ROLE_STATIC_PARAMS,
      }),
    quickFilterFields: [
      { field: 'name', label: 'Name' },
      { field: 'description', label: 'Description' },
    ],
    initialPageSize: 10,
    initialPinning: {
      left: ['select'],
      right: [],
    },
  });

  const { data: payload, status } = useUserRoles({
    searchParams: controller.searchParams,
  });

  const rows: UserRoleBasic[] = Array.isArray(payload)
    ? payload
    : (payload?.data ?? []);

  const meta = Array.isArray(payload) ? undefined : payload?.meta;

  return (
    <ResourceTableView<UserRoleBasic>
      label="User Roles"
      controller={controller}
      data={rows}
      meta={meta}
      status={status as any}
      onRowClick={(row) => navigate(`/user-roles/${row.id}`)}
    />
  );
};

export default UserRoleList;
